import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Table, Alert, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faUsers, 
  faPlus, 
  faEdit, 
  faTrash,
  faUserShield,
  faChartLine,
  faEye,
  faCheckCircle,
  faExclamationTriangle,
  faArrowUp,
  faArrowDown,
  faSignOutAlt,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { companyService } from '../../services/companyService';
import { userService } from '../../services/userService';
import { upgradeRequestService } from '../../services/upgradeRequestService';
import { useAuth } from '../../contexts/AuthContext';

const SuperAdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
    totalManagers: 0,
    recentOnboardings: 0,
    pendingRequests: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesData, usersData, requestsData] = await Promise.all([
          companyService.getAllCompanies(),
          userService.getAllUsers(),
          upgradeRequestService.getAllUpgradeRequests()
        ]);
        
        setCompanies(companiesData);
        setUsers(usersData);
        setUpgradeRequests(requestsData);
        
        // Calculate stats
        const activeCompanies = companiesData.filter(c => c.status === 'active').length;
        const managers = usersData.filter(u => u.role === 'manager').length;
        const recentOnboardings = companiesData.filter(c => {
          const onboardingDate = c.onboardingDate?.toDate();
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          return onboardingDate && onboardingDate > oneWeekAgo;
        }).length;
        const pendingRequests = requestsData.filter(r => r.status === 'pending').length;

        setStats({
          totalCompanies: companiesData.length,
          activeCompanies,
          totalUsers: usersData.length,
          totalManagers: managers,
          recentOnboardings,
          pendingRequests
        });
      } catch (error) {
        setError('Failed to fetch dashboard data');
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      try {
        await companyService.deleteCompany(companyId);
        setCompanies(companies.filter(company => company.id !== companyId));
        setStats(prev => ({
          ...prev,
          totalCompanies: prev.totalCompanies - 1,
          activeCompanies: prev.activeCompanies - 1
        }));
      } catch (error) {
        setError('Failed to delete company');
        console.error('Error deleting company:', error);
      }
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="secondary">Inactive</Badge>;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Super Admin Dashboard</h2>
          <p className="text-muted mb-0">Manage property management companies and system users</p>
        </div>
        <div className="d-flex gap-2">
          {stats.pendingRequests > 0 && (
            <Button as={Link} to="/admin/upgrade-requests" variant="warning">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Upgrade Requests ({stats.pendingRequests})
            </Button>
          )}
          <Button as={Link} to="/admin/upgrade-requests" variant="outline-warning">
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            All Requests
          </Button>
          <Button as={Link} to="/admin/companies/onboard" variant="primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Onboard New Company
          </Button>
          <Button as={Link} to="/admin/users/create" variant="outline-primary">
            <FontAwesomeIcon icon={faUsers} className="me-2" />
            Create User
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faBuilding} className="text-primary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.totalCompanies}</h4>
                  <p className="text-muted mb-0">Total Companies</p>
                  <small className="text-success">
                    <FontAwesomeIcon icon={faArrowUp} className="me-1" />
                    {stats.recentOnboardings} new this week
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.activeCompanies}</h4>
                  <p className="text-muted mb-0">Active Companies</p>
                  <ProgressBar 
                    now={(stats.activeCompanies / stats.totalCompanies) * 100} 
                    className="mt-2" 
                    style={{ height: '4px' }}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faUsers} className="text-info" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.totalUsers}</h4>
                  <p className="text-muted mb-0">Total Users</p>
                  <small className="text-info">
                    {stats.totalManagers} managers
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faUserShield} className="text-warning" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{users.filter(u => u.role === 'admin').length}</h4>
                  <p className="text-muted mb-0">System Admins</p>
                  <small className="text-warning">
                    Full system access
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faClock} className="text-info" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.pendingRequests}</h4>
                  <p className="text-muted mb-0">Pending Requests</p>
                  <small className="text-info">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    Needs attention
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Pending Upgrade Requests Section */}
      {stats.pendingRequests > 0 && (
        <Card className="border-0 shadow-sm mb-4 border-warning">
          <Card.Header className="bg-warning bg-opacity-10 border-0">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                Pending Upgrade Requests ({stats.pendingRequests})
              </h5>
              <Button as={Link} to="/admin/upgrade-requests" variant="warning" size="sm">
                View All Requests
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="row">
              {upgradeRequests.filter(r => r.status === 'pending').slice(0, 3).map((request) => (
                <div key={request.id} className="col-md-4 mb-3">
                  <div className="border rounded p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-1">Logo Upload Request</h6>
                      <Badge bg="warning">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        Pending
                      </Badge>
                    </div>
                    <p className="text-muted small mb-2">
                      Company: {request.companyId}
                    </p>
                    <p className="text-muted small mb-2">
                      Requested: {request.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </p>
                    <Button 
                      as={Link} 
                      to="/admin/upgrade-requests" 
                      variant="outline-warning" 
                      size="sm"
                    >
                      Review Request
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Quick Actions */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button as={Link} to="/admin/companies/onboard" variant="outline-primary" className="text-start">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Onboard New Property Management Company
                </Button>
                <Button as={Link} to="/admin/users/create" variant="outline-success" className="text-start">
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Create New User Account
                </Button>
                <Button as={Link} to="/admin/upgrade-requests" variant="outline-warning" className="text-start">
                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                  Manage Upgrade Requests
                  {stats.pendingRequests > 0 && (
                    <Badge bg="warning" className="ms-2">
                      {stats.pendingRequests}
                    </Badge>
                  )}
                </Button>
                <Button as={Link} to="/admin/companies" variant="outline-info" className="text-start">
                  <FontAwesomeIcon icon={faBuilding} className="me-2" />
                  View All Companies
                </Button>
                <Button as={Link} to="/admin/users" variant="outline-secondary" className="text-start">
                  <FontAwesomeIcon icon={faUserShield} className="me-2" />
                  Manage System Users
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">System Overview</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Company Onboarding Progress</span>
                  <span>{stats.activeCompanies}/{stats.totalCompanies}</span>
                </div>
                <ProgressBar 
                  now={(stats.activeCompanies / Math.max(stats.totalCompanies, 1)) * 100} 
                  variant="success"
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>User Management</span>
                  <span>{stats.totalManagers} managers</span>
                </div>
                <ProgressBar 
                  now={(stats.totalManagers / Math.max(stats.totalUsers, 1)) * 100} 
                  variant="info"
                />
              </div>
              <div className="text-center">
                <small className="text-muted">
                  System is running smoothly with {stats.activeCompanies} active companies
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Companies */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Recent Companies</h5>
            <Button as={Link} to="/admin/companies" variant="outline-primary" size="sm">
              View All Companies
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {companies.length > 0 ? (
            <Table responsive hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Company Name</th>
                  <th>Location</th>
                  <th>Contact Person</th>
                  <th>Status</th>
                  <th>Onboarding Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.slice(0, 5).map((company) => (
                  <tr key={company.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faBuilding} className="text-primary me-2" />
                        <div>
                          <div className="fw-bold">{company.name}</div>
                          <small className="text-muted">{company.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>{company.location}</td>
                    <td>
                      <div>
                        <div>{company.contactPerson}</div>
                        <small className="text-muted">{company.contactPhone}</small>
                      </div>
                    </td>
                    <td>{getStatusBadge(company.status)}</td>
                    <td>
                      {company.onboardingDate ? 
                        company.onboardingDate.toDate().toLocaleDateString() : 
                        'N/A'
                      }
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/admin/companies/${company.id}`)}
                          title="View Details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => navigate(`/admin/companies/${company.id}/edit`)}
                          title="Edit Company"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteCompany(company.id)}
                          title="Delete Company"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faBuilding} className="text-muted mb-3" size="3x" />
              <h6 className="text-muted">No companies yet</h6>
              <p className="text-muted mb-3">Start by onboarding your first property management company</p>
              <Button as={Link} to="/admin/companies/onboard" variant="primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Onboard First Company
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* System Users */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">System Users</h5>
            <Button as={Link} to="/admin/users" variant="outline-primary" size="sm">
              Manage System Users
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {users.length > 0 ? (
            <Table responsive hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.slice(0, 5).map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" 
                               style={{ width: '40px', height: '40px' }}>
                            <FontAwesomeIcon icon={faUsers} className="text-primary" />
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <div className="fw-bold">{user.name}</div>
                          <small className="text-muted">{user.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {user.role === 'admin' ? 
                        <Badge bg="danger">Admin</Badge> : 
                        <Badge bg="info">Manager</Badge>
                      }
                    </td>
                    <td>
                      {user.role === 'manager' ? 
                        companies.find(c => c.id === user.companyId)?.name || 'N/A' : 
                        <span className="text-muted">System Admin</span>
                      }
                    </td>
                    <td>
                      {user.status === 'active' ? 
                        <Badge bg="success">Active</Badge> : 
                        <Badge bg="secondary">Inactive</Badge>
                      }
                    </td>
                    <td>
                      {user.createdAt ? 
                        user.createdAt.toDate().toLocaleDateString() : 
                        'N/A'
                      }
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          title="View Details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                          title="Edit User"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faUsers} className="text-muted mb-3" size="3x" />
              <h6 className="text-muted">No users yet</h6>
              <p className="text-muted mb-3">Start by creating your first system user</p>
              <Button as={Link} to="/admin/users/create" variant="primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create First User
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard; 